import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Divider,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { loginSchema } from "./validations";
import { loginApi } from "../../Services/authService";
import { loginUser } from "../../store/reducers/auth";
import { whiteLogo, FooterLogo } from "../../assets";
import { ErrorText } from "../../Components/index";

// Custom color constants
const primaryBlue = "#0e3584";

export function Login({ handleForgotPassword }) {
  const form = useForm({
    resolver: yupResolver(loginSchema),
    mode: "all",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = form;
  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleLogin = async (data) => {
    setIsLoading(true);
    setInvalidCredentials(false);
    setErrorMessage("");

    try {
      const email = data?.email.toLowerCase();
      const res = await loginApi(email, data?.password);

      if (res.status === 200) {
        const userData = res.data;
        const userRole = userData?.user?.role?.trim()?.toLowerCase();

        if (userRole === "admin" || userRole === "vkyc auditor") {
          dispatch(loginUser(userData));
          navigate("/vkyc/dashboard", { replace: true });
        } else {
          setInvalidCredentials(true);
          setErrorMessage(`Access denied: "${userData.role}" is not authorized.`);
        }
      } else {
        setInvalidCredentials(true);
        setErrorMessage("Invalid email or password.");
      }
    } catch (error) {
      console.error("Login error:", error);
      setInvalidCredentials(true);
      const backendMsg = error?.response?.data?.error?.message;
      setErrorMessage(backendMsg || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Reset invalid credentials message on form change
  useEffect(() => {
    if (watch("email") === "" || watch("password") === "") {
      setInvalidCredentials(false);
    }
  }, [watch("email"), watch("password")]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        bgcolor: primaryBlue,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
        justifyContent: "center",
        overflow: "hidden",
        p: 0,
        m: 0,
      }}
    >
      {/* Left panel */}
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start", 
          justifyContent: "center",
          pl: { xs: 2, md: 12, lg: 20 },
          py: { xs: 4, md: 0 },
          color: "#fff",
          bgcolor: primaryBlue,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#fff" }}>
          Intelli VKYC
        </Typography>
        <Typography
          sx={{
            fontSize: 18,
            opacity: 0.9,
            mb: 4,
            color: "#fff",
            textAlign: "left",
          }}
        >
          Unified Verification for Modern Lending. Access APIs with confidence – compliant, secure, and fraud-free.
        </Typography>
        <Stack spacing={2}>
          {[
            { icon: "✔️", text: "Compliance-first onboarding" },
            { icon: "⚡", text: "Faster fraud detection" },
            { icon: "🔗", text: "Access multiple APIs seamlessly" },
            { icon: "🏦", text: "Trusted by leading BFIs & NBFCs" },
          ].map((item, index) => (
            <Typography
              key={index}
              sx={{
                fontSize: 18,
                opacity: 0.9,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "flex-start",
              }}
            >
              <span role="img" aria-label="icon">
                {item.icon}
              </span>{" "}
              {item.text}
            </Typography>
          ))}
        </Stack>
      </Box>

      {/* Right panel - Login form */}
      <Box
        sx={{
          width: { xs: "100%", sm: "90%", md: "50%", lg: "35%" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "transparent",
          py: { xs: 4, md: 0 },
        }}
      >
        <Paper
          elevation={4}
          sx={{
            borderRadius: "20px",
            px: { xs: 2, sm: 8 },
            py: { xs: 4, sm: 8 },
            width: "100%",
            maxWidth: 400,
            boxSizing: "border-box",
            minHeight: 250,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={600}
            textAlign="center"
            mb={3}
            sx={{ fontSize: 18 }}
          >
            Secure access to Intelli VKYC
          </Typography>
          <form onSubmit={handleSubmit(handleLogin)} autoComplete="off">
            <Stack spacing={1}>
              {invalidCredentials && <ErrorText>{errorMessage}</ErrorText>}
              <TextField
                fullWidth
                label="Email address"
                id="email"
                type="email"
                placeholder="Enter your email"
                {...register("email")}
                error={!!errors.email}
                helperText={errors.email?.message}
                InputLabelProps={{ shrink: true, style: { fontSize: 14, color: "#555" } }}
                autoComplete="email"
                sx={{
                  mb: 2.5,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: 14,
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 1,
                  },
                  "& .MuiInputBase-input": {
                    padding: "0 12px",
                    fontSize: 14,
                    textAlign: "left",
                    lineHeight: 1,
                  },
                  "& .MuiInputBase-input::placeholder": {
                    fontSize: 14,
                    color: "#9e9e9e",
                    opacity: 1,
                  },
                }}
              />
              <TextField
                fullWidth
                label="Password"
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                {...register("password")}
                error={!!errors.password}
                helperText={errors.password?.message}
                InputLabelProps={{ shrink: true, style: { fontSize: 14, color: "#555" } }}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      aria-label="toggle password visibility"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  ),
                }}
                sx={{
                  mb: 2.5,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: 14,
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 1,
                  },
                  "& .MuiInputBase-input": {
                    padding: "0 12px",
                    fontSize: 14,
                    textAlign: "left",
                    lineHeight: 1,
                  },
                  "& .MuiInputBase-input::placeholder": {
                    fontSize: 14,
                    color: "#9e9e9e",
                    opacity: 1,
                  },
                }}
              />
              <Button
                sx={{ alignSelf: "flex-end", textTransform: "none", fontSize: "12px" }}
                onClick={handleForgotPassword}
                size="small"
              >
                Forgot password?
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ fontSize: 18, fontWeight: 600, py: 1.5, mt: 1 }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : "Sign In"}
              </Button>
            </Stack>
          </form>
          <Divider sx={{ my: 5, fontSize: "16px", fontWeight: 500 }}>or</Divider>
          <Typography mt={2} sx={{ textAlign: "center", fontSize: "16px" }}>
            Don&apos;t Have Account?{" "}
            <Link
              to="https://www.habilelabs.io/contact-us"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#1976d2",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                e.target.style.color = "#1976d2";
                e.target.style.textDecoration = "underline";
              }}
              onMouseLeave={(e) => {
                e.target.style.color = "#1976d2";
                e.target.style.textDecoration = "none";
              }}
            >
              Contact us
            </Link>
          </Typography>
          <Box mt={3} textAlign="center">
            <Typography variant="body2" color="text.secondary" fontSize={16}>
              powered by{" "}
              <a
                href="https://www.habilelabs.io/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#2ab6e5", fontWeight: 500, textDecoration: "none" }}
              >
                HabileLabs
              </a>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
